import { Core } from "@faire/design-tokens";
import { Language } from "@faire/web-api/indigofair/languages/Language";
import { useStrictLocalization } from "@faire/web/common/localization";
import { useIsSSR } from "@faire/web/common/server-side-rendering/isSSR";
import { optimizeImageUrl } from "@faire/web/common/transform-images";
import { Column, Flex, Row } from "@faire/web/slate/Layout";
import { getSpacing } from "@faire/web/slate/spacing";
import { responsiveMediaQueries } from "@faire/web/slate/Theme/responsiveMediaQueries";
import { Typography } from "@faire/web/slate/Typography";
import { StrictLocalMsg } from "@faire/web/ui/lib/localization";
import React from "react";
import styled from "styled-components";

import { getLocaleKey } from "@faire/retailer/serialized-data/getLocaleKey";

export const IncentiveTestimonials: React.FC<{ backgroundColor: string }> = ({
  backgroundColor,
}) => {
  const { strictLocalize } = useStrictLocalization();
  return (
    <BackgroundColorWrapper $backgroundColor={backgroundColor}>
      <Wrapper>
        <Typography color={Core.text.default} variant="displaySSerifRegular">
          <StrictLocalMsg
            defaultMessage="What retailers are saying"
            description={{
              text: "Header for retailer comment section",
              img: "https://cdn.faire.com/fastly/a31e0fb4b90d0e6714de727bfa58181284e716b2c0fac717317c12d31fd21550.png",
            }}
          />
        </Typography>
        <ItemListContainer gap="6x">
          <TestimonialItem
            name="Jake France + Richard Rolfe"
            retailerName="Boy's Fort"
            location={strictLocalize({
              defaultMessage: "Portland, Oregon",
              description: {
                text: "Location for the first retailer comment",
                img: "https://cdn.faire.com/fastly/a31e0fb4b90d0e6714de727bfa58181284e716b2c0fac717317c12d31fd21550.png",
              },
            })}
            imageUrl="https://cdn.faire.com/fastly/9cd7f99ab4862a8332e44f3876063e11cdf0a37efa2f634b3bc5b7c8a0fc3ca0.png"
            testimonialText={strictLocalize({
              defaultMessage:
                "Faire has allowed us to easily try products that we might not have taken the chance on before. The fact that they’ve tailored the selections to fit our brand has simplified our buying processes significantly.",
              description: {
                text: "Comment from the first retailer",
                img: "https://cdn.faire.com/fastly/a31e0fb4b90d0e6714de727bfa58181284e716b2c0fac717317c12d31fd21550.png",
              },
            })}
          />
          <TestimonialItem
            name="Cat Seto"
            retailerName="Ferme à Papier"
            location={strictLocalize({
              defaultMessage: "San Francisco, California",
              description: {
                text: "Location for the second retailer comment",
                img: "https://cdn.faire.com/fastly/a31e0fb4b90d0e6714de727bfa58181284e716b2c0fac717317c12d31fd21550.png",
              },
            })}
            imageUrl="https://cdn.faire.com/fastly/fad01eaf8a0151e3a3be4cab67748b85accdcb9353df4e2d8068a89e421aa57f.png"
            testimonialText={strictLocalize({
              defaultMessage:
                "The team at Faire understands the busy lives of its indie shop owners and makers and has made the process of buying and selling an efficient and dynamic experience.",
              description: {
                text: "Comment from the second retailer",
                img: "https://cdn.faire.com/fastly/a31e0fb4b90d0e6714de727bfa58181284e716b2c0fac717317c12d31fd21550.png",
              },
            })}
          />
        </ItemListContainer>
      </Wrapper>
    </BackgroundColorWrapper>
  );
};

interface IProps {
  name: string;
  retailerName: string;
  location: string;
  imageUrl: string;
  testimonialText: string;
}

const TestimonialItem: React.FC<IProps> = ({
  name,
  retailerName,
  location,
  imageUrl,
  testimonialText,
}) => {
  const isSSR = useIsSSR();
  const language = getLocaleKey()?.language;

  return (
    <TestimonialItemWrapper>
      {language === Language.SPA ? (
        <TestimonialText>{`«${testimonialText}»`}</TestimonialText>
      ) : (
        <TestimonialText>{`“${testimonialText}”`}</TestimonialText>
      )}
      <Row gap="2x" align="center">
        <Image src={optimizeImageUrl({ url: imageUrl }, isSSR)} />
        <Column gap={4}>
          <Typography color={Core.text.default} variant="paragraphSansMedium">
            {name}
          </Typography>
          <LineValue>{retailerName}</LineValue>
          <LineValue>{location}</LineValue>
        </Column>
      </Row>
    </TestimonialItemWrapper>
  );
};

const ItemListContainer = styled(Flex).attrs({
  direction: { mobileAndAbove: "column", desktopAndAbove: "row" },
})``;

const TestimonialItemWrapper = styled(Column).attrs({
  gap: "2x",
  justify: "space-between",
  align: "flex-start",
})`
  max-width: 500px;
  ${responsiveMediaQueries.desktopAndAbove} {
    width: 427px;
    min-height: 259px;
  }
`;

const TestimonialText = styled(Typography).attrs({
  color: Core.text.default,
  variant: "pageHeaderSerifRegular",
})`
  text-align: left;
`;

const BackgroundColorWrapper = styled.div<{ $backgroundColor: string }>`
  width: 100vw;
  background-color: ${({ $backgroundColor }) => $backgroundColor};
`;

const Wrapper = styled(Column).attrs({
  align: "center",
  gap: "9x",
})`
  ${responsiveMediaQueries.tabletAndBelow} {
    padding: ${getSpacing("6x", "3x")};
  }
  ${responsiveMediaQueries.desktopAndAbove} {
    padding: ${getSpacing("9x", 0)};
  }
`;

const Image = styled.img`
  width: 75px;
  height: 75px;
  object-fit: cover;
`;

const LineValue = styled(Typography).attrs({
  color: Core.text.subdued,
})``;
